export type FeatureRuntimePhase = "render" | "support" | "apply" | "reset";

export type NormalizedFeatureRuntimeError = {
  errorMessage: string;
  errorType: string;
  featureKey: string;
  phase: FeatureRuntimePhase;
};

export type NormalizeFeatureRuntimeErrorInput = {
  error: unknown;
  featureKey: string;
  phase: FeatureRuntimePhase;
};

const maxMessageLength = 120;

function sanitizeMessage(message: string) {
  const sanitized = message
    .replace(/https?:\/\/\S+/gi, "[redacted-url]")
    .replace(/\b\S+@\S+\.\S+\b/g, "[redacted-email]")
    .replace(/\/[A-Za-z0-9._~!$&'()*+,;=:@%-]+(?:\/[A-Za-z0-9._~!$&'()*+,;=:@%-]+)*(?:\?[^\s]*)?/g, "[redacted-path]")
    .replace(/[?#][^\s]+/g, "[redacted-url-part]")
    .replace(/(^|\s)[#.][A-Za-z0-9_-]+/g, "$1[redacted-selector]")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.slice(0, maxMessageLength) || "Runtime error";
}

export function normalizeFeatureRuntimeError({
  error,
  featureKey,
  phase,
}: NormalizeFeatureRuntimeErrorInput): NormalizedFeatureRuntimeError {
  if (error instanceof Error) {
    return {
      errorMessage: sanitizeMessage(error.message),
      errorType: error.name || "Error",
      featureKey,
      phase,
    };
  }

  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return {
      errorMessage: sanitizeMessage(error.message),
      errorType: error.name || "DOMException",
      featureKey,
      phase,
    };
  }

  if (typeof error === "string") {
    return {
      errorMessage: sanitizeMessage(error),
      errorType: "Error",
      featureKey,
      phase,
    };
  }

  return {
    errorMessage: "Unknown runtime error",
    errorType: "UnknownError",
    featureKey,
    phase,
  };
}
